import { queries } from '@wiztime/storage';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useServices } from '../../hooks/useServices';
import { useLocalMode } from '../../hooks/useLocalMode';
import { useSyncConfigured } from '../../hooks/useSyncStatus';
import { clearFirstSyncExpected, isFirstSyncExpected, subscribeFirstSyncExpected } from '../../lib/firstSyncWait';
import { EntryKind, gateApplies } from './gateModel';
import { FirstSyncPending } from './FirstSyncPending';
import { WelcomePage } from './WelcomePage';

/**
 * Guards the app routes in a browser: while sync is not configured, only the welcome page is
 * available. Renders nothing while it finds out (no flash of the page for a configured device).
 * Print routes, the Tauri desktop shell and the test-only bypass pass through.
 */
export function WelcomeGate({ children }: { readonly children: ReactNode }) {
  const { pathname } = useLocation();
  const { store, sync } = useServices();
  const configured = useSyncConfigured();
  const localMode = useLocalMode();
  const applies = gateApplies(pathname);
  // Records on this device, read once while the page is needed; null until known.
  const [localCount, setLocalCount] = useState<number | null>(null);
  // Set after logging in to an existing account, until its first sync is over.
  const [waitingForFirstSync, setWaitingForFirstSync] = useState(false);
  const [recordsBefore, setRecordsBefore] = useState(0);
  // Same wait after logging in or joining from Settings in local mode (the welcome page was not involved).
  const settingsJoin = useSyncExternalStore(subscribeFirstSyncExpected, isFirstSyncExpected, () => false);
  // The stored flag of a device that joined an existing space and has not read it yet: it survives a reload.
  // Latched while the wait screen shows, because the engine clears the flag before the sync run is over;
  // `dismissed` keeps a user who went on after a failure from being held again while the flag stays set.
  const storedPending = useSyncExternalStore(sync.subscribe, sync.isFirstSyncPending, () => false);
  const [latched, setLatched] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  if (storedPending && !latched && !dismissed) setLatched(true);
  if (!storedPending && dismissed) setDismissed(false);
  const entering = useRef(false);
  const needsCount = applies && !configured && !localMode && localCount === null;

  useEffect(() => {
    if (!needsCount) return;
    let alive = true;
    queries.allRecords(store.repo).then(
      (records) => {
        if (alive) setLocalCount(records.length);
      },
      (error: unknown) => {
        console.error('could not count the local records', error);
        if (alive) setLocalCount(0);
      },
    );
    return () => {
      alive = false;
    };
  }, [needsCount, store]);

  // Whether the local records are uploaded is decided when sync is activated (the sync manager asks when they
  // belong to another account); nothing is requested here. The count is read again after the welcome page
  // is left, because activation may have cleared the device.
  // Local mode is entered: forget the count of the welcome page that led into it, so the page counts the
  // records again once local mode ends (data cleared, or sync activated).
  const [wasLocalMode, setWasLocalMode] = useState(localMode);
  if (localMode !== wasLocalMode) {
    setWasLocalMode(localMode);
    if (localMode) setLocalCount(null);
  }

  const onEntered = useCallback(
    (kind: EntryKind) => {
      if (entering.current) return;
      entering.current = true;
      const leave = (before: number): void => {
        entering.current = false;
        setRecordsBefore(before);
        setWaitingForFirstSync(kind === EntryKind.Existing);
        // Leaves the welcome page; counted again if sync is turned off later or the local data is cleared.
        setLocalCount(null);
      };
      queries.allRecords(store.repo).then(
        (records) => leave(records.length),
        (error: unknown) => {
          console.error('could not count the local records', error);
          leave(localCount ?? 0);
        },
      );
    },
    [localCount, store],
  );

  if (!applies) return <>{children}</>;
  // Local mode (the user's choice on the welcome page): the app opens without sync. The count is read again
  // when the mode ends, so the welcome page never shows a stale number.
  if (localMode && !waitingForFirstSync && !settingsJoin && !latched) return <>{children}</>;
  if (waitingForFirstSync || settingsJoin || latched) {
    return (
      <FirstSyncPending
        recordsBefore={recordsBefore}
        onDone={() => {
          setWaitingForFirstSync(false);
          clearFirstSyncExpected();
          setLatched(false);
          if (sync.isFirstSyncPending()) setDismissed(true);
        }}
      />
    );
  }
  // The page stays up once shown, also after sync turned on, until the user finishes the flow.
  if (localCount !== null) return <WelcomePage localCount={localCount} onEntered={onEntered} />;
  if (configured) return <>{children}</>;
  return null;
}
